import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';

interface SeatUsageBarProps {
  used: number;
  limit: number;
  label: string;
  /**
   * Seats whose windows have all passed. They still occupy a row, but the next client to ask
   * gets one, so they are not in use and must not be counted as though they were.
   */
  reclaimable?: number;
}

/**
 * "SuPAR 8/20" with a bar, so a nearly-full pool is visible at a glance.
 *
 * The width is fixed rather than content-driven: the column is repeated on every license
 * card, and letting "Composer 0/5" and "SuPAR 12/100" size themselves would leave each
 * card's bars — and the action column after them — starting at a different x.
 *
 * **`used` counts seats still held, never rows that exist.** Expired rows are never swept in
 * the background and are reclaimed one at a time when somebody asks, so counting them made a
 * pool read as full indefinitely once its users had quit. That is the opposite of what this
 * screen is for: "full, people are being refused" and "nobody is using it" looked identical.
 * Reclaimable seats are shown as a lighter section of the bar instead, because an operator
 * needs to see that the headroom is there without having to do anything to get it.
 */
const SeatUsageBar = ({ used, limit, label, reclaimable = 0 }: SeatUsageBarProps) => {
  const isLicensed = limit > 0;
  const ratio = isLicensed ? Math.min(100, (used / limit) * 100) : 0;
  // Capped against what is left after the held seats, so a stale row can never push the bar
  // past full and imply a pool is oversold.
  const reclaimableRatio = isLicensed
    ? Math.min(100 - ratio, (reclaimable / limit) * 100)
    : 0;

  const color = !isLicensed ? 'primary' : ratio >= 100 ? 'error' : ratio >= 80 ? 'warning' : 'primary';

  const tooltip = !isLicensed
    ? `${label}: not licensed`
    : reclaimable > 0
      ? `${label}: ${used} of ${limit} seats in use, ${reclaimable} idle and free for the next client`
      : `${label}: ${used} of ${limit} seats in use`;

  return (
    <Tooltip title={tooltip}>
      <Box sx={{ width: '100%' }}>
        {/* Label left, count right — a block row, so the two bars line up with each other. */}
        <Typography
          component='div'
          variant='caption'
          color='text.secondary'
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, lineHeight: 1.6 }}
        >
          <span>{label}</span>
          <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {used}/{limit}
            {reclaimable > 0 && (
              <Box component='span' sx={{ opacity: 0.6, ml: 0.5 }}>
                +{reclaimable} idle
              </Box>
            )}
          </Box>
        </Typography>

        {/* One track, two segments: held seats solid, reclaimable ones faded behind them. */}
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant='determinate'
            value={ratio + reclaimableRatio}
            color={color}
            sx={{ height: 6, borderRadius: 1, opacity: isLicensed ? 0.3 : 0.15 }}
          />
          <LinearProgress
            variant='determinate'
            value={ratio}
            color={color}
            sx={{
              height: 6,
              borderRadius: 1,
              opacity: isLicensed ? 1 : 0.3,
              position: 'absolute',
              inset: 0,
              backgroundColor: 'transparent'
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default SeatUsageBar;
